import React from 'react';
import { useTranslation } from 'react-i18next';

const TimeSlotStats = ({ timeSlotData, isLoading }) => {
  const { t } = useTranslation();

  // SkeletonBox component for loading state
  const SkeletonBox = ({ className }) => (
    <div
      className={`bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse ${className}`}></div>
  );

  // Skeleton loading for time slots column
  const SkeletonTimeSlots = ({ label }) => {
    return (
      <div className="mb-8 md:mb-0">
        <h3 className="text-xl font-semibold mb-4">{label}</h3>

        <div className="bg-blue-50 rounded-lg p-3 md:p-4">
          <div className="flex justify-between mb-2">
            <span className="font-semibold text-xs">Heure</span>
            <div className="flex space-x-2 md:space-x-3 lg:space-x-4">
              <span className="font-semibold text-xs">Arrivées</span>
              <span className="font-semibold text-xs">Pique-niques</span>
              <span className="font-semibold text-xs">Départs</span>
            </div>
          </div>

          {/* Skeleton slots */}
          {[...Array(8)].map((_, index) => (
            <div key={index} className="flex justify-between py-1 text-xs font-semibold px-2 -mx-2">
              <div className="h-3 bg-gradient-to-r from-blue-100 to-blue-200 rounded animate-pulse w-12 md:w-16"></div>
              <div className="flex space-x-2 md:space-x-3 lg:space-x-8">
                <div className="h-3 bg-gradient-to-r from-blue-100 to-blue-200 rounded animate-pulse w-6 md:w-8"></div>
                <div className="h-3 bg-gradient-to-r from-blue-100 to-blue-200 rounded animate-pulse w-6 md:w-8"></div>
                <div className="h-3 bg-gradient-to-r from-blue-100 to-blue-200 rounded animate-pulse w-6 md:w-8"></div>
              </div>
            </div>
          ))}

          <hr className="border-gray-300 my-2" />
          <div className="flex justify-between text-xs font-semibold px-2 -mx-2">
            <span>Total</span>
            <div className="flex space-x-2 md:space-x-3 lg:space-x-8">
              <div className="h-3 bg-gradient-to-r from-blue-100 to-blue-200 rounded animate-pulse w-6 md:w-8"></div>
              <div className="h-3 bg-gradient-to-r from-blue-100 to-blue-200 rounded animate-pulse w-6 md:w-8"></div>
              <div className="h-3 bg-gradient-to-r from-blue-100 to-blue-200 rounded animate-pulse w-6 md:w-8"></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="mt-8 md:mt-12">
        <h2 className="text-sm md:text-base text-gray-800 mb-4 px-2 md:px-0">
          Total par heure d'arrivée : Inscriptions d'établissements / organisations + inscriptions
          d'une unité
        </h2>
        <div className="border border-gray-300 rounded-lg p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 text-gray-700">
            <SkeletonTimeSlots label="16.10" />
            <SkeletonTimeSlots label="17.10" />
            <SkeletonTimeSlots label="18.10" />
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!timeSlotData) {
    return (
      <div className="mt-8 md:mt-12">
        <h2 className="text-sm md:text-base text-gray-800 mb-4 px-2 md:px-0">
          Total par heure d'arrivée : Inscriptions d'établissements / organisations + inscriptions
          d'une unité
        </h2>
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 text-center text-gray-500 border border-gray-300">
          {t('timeSlotStats.noData') || 'Aucune donnée disponible'}
        </div>
      </div>
    );
  }

  // Format time for display (e.g. 09:00 → 09h00)
  const formatTime = (time) => {
    return time.replace(':', 'h');
  };

  // Function to render a single time slot column
  const renderTimeSlotColumn = (dayData) => {
    if (!dayData || !dayData.slots) return null;

    const slots = dayData.slots;
    const totals = dayData.totals || { arrival: 0, picnic: 0, departure: 0 };

    return (
      <div className="bg-blue-50 rounded-lg p-3 md:p-4 overflow-hidden">
        <div className="flex justify-between mb-2">
          <span className="font-semibold text-xs">Heure</span>
          <div className="flex space-x-2 md:space-x-3 lg:space-x-4">
            <span className="font-semibold text-xs w-10 md:w-12 lg:w-16 text-center">Arrivées</span>
            <span className="font-semibold text-xs w-10 md:w-12 lg:w-16 text-center">
              Pique-niques
            </span>
            <span className="font-semibold text-xs w-10 md:w-12 lg:w-16 text-center">Départs</span>
          </div>
        </div>

        {slots.map((slot, index) => {
          return (
            <div key={index} className="flex justify-between py-1 text-xs font-semibold px-2 -mx-2">
              <span className="text-gray-700">{formatTime(slot.time)}</span>
              <div className="flex space-x-2 md:space-x-3 lg:space-x-4">
                <span className="w-10 md:w-12 lg:w-16 text-center">
                  {slot.arrival > 0 ? slot.arrival : '-'}
                </span>
                <span className="w-10 md:w-12 lg:w-16 text-center">
                  {slot.picnic > 0 ? slot.picnic : '-'}
                </span>
                <span className="w-10 md:w-12 lg:w-16 text-center">
                  {slot.departure > 0 ? slot.departure : '-'}
                </span>
              </div>
            </div>
          );
        })}

        <hr className="border-gray-300 my-2" />
        <div className="flex justify-between text-xs font-semibold px-2 -mx-2">
          <span>Total</span>
          <div className="flex space-x-2 md:space-x-3 lg:space-x-4">
            <span className="w-10 md:w-12 lg:w-16 text-center">{totals.arrival}</span>
            <span className="w-10 md:w-12 lg:w-16 text-center">{totals.picnic}</span>
            <span className="w-10 md:w-12 lg:w-16 text-center">{totals.departure}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-8 md:mt-12">
      <h2 className="text-sm md:text-base text-gray-800 mb-4 px-2 md:px-0">
        Total par heure d'arrivée : Inscriptions d'établissements / organisations + inscriptions
        d'une unité
      </h2>

      <div className="border border-gray-300 rounded-lg p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 lg:gap-6 text-gray-700">
          {/* Thu column */}
          <div className="mb-6 md:mb-0">
            <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">
              {timeSlotData.thu?.label || '16.10'}
            </h3>
            {renderTimeSlotColumn(timeSlotData.thu)}
          </div>

          {/* Fri column */}
          <div className="mb-6 md:mb-0">
            <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">
              {timeSlotData.fri?.label || '17.10'}
            </h3>
            {renderTimeSlotColumn(timeSlotData.fri)}
          </div>

          {/* Sat column */}
          <div>
            <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">
              {timeSlotData.sat?.label || '18.10'}
            </h3>
            {renderTimeSlotColumn(timeSlotData.sat)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeSlotStats;
